/**
 * GearIntakeFlow (mobile RN) — the MoyRi gear-table diagnostic (Spine §3).
 * One question per screen from GEAR_INTAKE[gear], 3 min / 10 baseline / 20 max,
 * folding to the gear's most-active malady + the tool it displaces. Style
 * mirrors SpectrumIntake.tsx.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Image, ImageSourcePropType, Pressable, StyleSheet, Text, View } from 'react-native';
import { supabase } from '../lib/supabase';
import { DISPLAY_FONT, makeThemedStyles, useTheme } from '../v10/theme';
import { GlassCard } from '../v10/ui';
import { GEAR_META, type Gear } from '../domain/olmps';
import {
  GEAR_INTAKE_MIN,
  GEAR_INTAKE_BASELINE,
  GEAR_INTAKE_MAX,
  type GearIntakeResult,
} from './spine-types';
import { GEAR_INTAKE, foldGearIntake } from './gearIntake';
import { saveGearIntake } from './spine-data';
import { saveIntake } from './spectrum-data';
import { getTool, type DmtToolId } from './spectrum-tools';
import { dmtArt, maladyArt } from './deityArt';
import type { GearIntakeOption } from './spine-types';

export function GearIntakeFlow({
  userId,
  gear,
  onDone,
}: {
  userId: string | undefined;
  gear: Gear;
  onDone: (result: GearIntakeResult) => void;
}) {
  const styles = useStyles();
  const questions = GEAR_INTAKE[gear];
  const [i, setI] = useState(0);
  const [chosen, setChosen] = useState<GearIntakeOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<GearIntakeResult | null>(null);

  const question = questions[i];
  const atMax = i === GEAR_INTAKE_MAX - 1;
  const canFinishHere = chosen.length >= GEAR_INTAKE_MIN;

  // One-time pop the moment Submit becomes available, so it doesn't hide.
  const pulse = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    if (!canFinishHere) return;
    pulse.setValue(1);
    Animated.sequence([
      Animated.timing(pulse, { toValue: 1.16, duration: 170, useNativeDriver: true }),
      Animated.spring(pulse, { toValue: 1, friction: 3.5, tension: 140, useNativeDriver: true }),
    ]).start();
  }, [canFinishHere, pulse]);

  const finish = async (finalChosen: GearIntakeOption[]) => {
    if (!userId) return;
    setSaving(true);
    const folded = foldGearIntake(gear, finalChosen);
    await saveIntake(
      supabase,
      userId,
      folded.toolId as DmtToolId,
      { picks: finalChosen.map((o) => o.label) },
      folded.subtype,
      folded.position,
    );
    await saveGearIntake(supabase, userId, gear, folded);
    setResult(folded);
    setSaving(false);
  };

  const pick = (opt: GearIntakeOption) => {
    const next = [...chosen, opt];
    setChosen(next);
    if (atMax) {
      finish(next);
      return;
    }
    setI(i + 1);
  };

  const finishNow = () => {
    if (chosen.length < GEAR_INTAKE_MIN) return;
    finish(chosen);
  };

  if (result) {
    const tool = getTool(result.toolId as DmtToolId);
    const malady = tool.maladies.find((mm) => mm.id === result.subtype)?.name ?? tool.maladies[0]?.name ?? '';
    return (
      <View style={styles.container}>
        <Text style={styles.kicker}>{GEAR_META[gear].name}</Text>
        <View style={styles.matchup}>
          <ArtSquare uri={maladyArt(result.subtype)} name={malady} tone="malady" caption="MALA-D" />
          <Text style={styles.versus}>vs.</Text>
          <ArtSquare uri={dmtArt(result.toolId)} name={tool.name} tone="dmt" caption="DMT" />
        </View>
        <Text style={styles.resultLine}>This becomes what you work on for this gear.</Text>
        <Pressable style={styles.doneButton} onPress={() => onDone(result)}>
          <Text style={styles.doneButtonText}>Continue</Text>
        </Pressable>
      </View>
    );
  }

  const finishLabel =
    chosen.length + 1 <= GEAR_INTAKE_MIN
      ? null
      : chosen.length + 1 <= GEAR_INTAKE_BASELINE
        ? 'Submit now — accuracy compromised'
        : 'Submit now — baseline accuracy';

  return (
    <View style={styles.container}>
      <Text style={styles.kicker}>
        {GEAR_META[gear].name} · question {i + 1} of {questions.length}
      </Text>
      <Text style={styles.prompt}>{question.prompt}</Text>

      <View style={styles.options}>
        {question.options.map((opt) => (
          <Pressable key={opt.label} disabled={saving} onPress={() => pick(opt)}>
            <GlassCard style={styles.option}>
              <Text style={styles.optionText}>{opt.label}</Text>
            </GlassCard>
          </Pressable>
        ))}
      </View>

      {canFinishHere && finishLabel && (
        <Animated.View style={{ transform: [{ scale: pulse }] }}>
          <Pressable disabled={saving} onPress={finishNow} style={styles.finishButton}>
            <Text style={styles.finishButtonText}>{finishLabel}</Text>
          </Pressable>
        </Animated.View>
      )}

      <Text style={styles.footer}>
        No grade, no shame — this just marks where you are so progress from here is the game.
      </Text>
    </View>
  );
}

// A square art tile for a malady or DMT. Falls back to the name centered on a
// tinted tile when no image URL exists yet (art is added one deity at a time).
function ArtSquare({
  uri,
  name,
  tone,
  caption,
}: {
  uri: ImageSourcePropType | undefined;
  name: string;
  tone: 'malady' | 'dmt';
  caption: string;
}) {
  const { t } = useTheme();
  const styles = useStyles();
  const accent = tone === 'malady' ? t.maladyRed : t.ivorySoft;
  return (
    <View style={styles.artCol}>
      <View style={[styles.artSquare, { borderColor: accent }]}>
        {uri ? (
          <Image source={uri} style={styles.artImage} resizeMode="cover" />
        ) : (
          <View style={styles.artFallback}>
            <Text style={[styles.artFallbackName, { color: accent }]}>{name}</Text>
          </View>
        )}
      </View>
      <Text style={[styles.artCaption, { color: accent }]}>{caption}</Text>
      <Text style={styles.artName} numberOfLines={1}>{name}</Text>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    container: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 20, padding: 24 },
    matchup: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 12 },
    versus: { fontSize: 13, fontWeight: '700', color: t.ivoryFaint },
    artCol: { alignItems: 'center', gap: 6, width: 132 },
    artSquare: {
      width: 132,
      height: 132,
      borderRadius: 22,
      borderWidth: 2,
      overflow: 'hidden',
      backgroundColor: t.navyRaised,
    },
    artImage: { width: '100%', height: '100%' },
    artFallback: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 10 },
    artFallbackName: { fontSize: 16, fontWeight: '800', textAlign: 'center' },
    artCaption: { fontSize: 10, fontWeight: '800', letterSpacing: 1.5 },
    artName: { fontSize: 14, fontWeight: '700', color: t.ivorySoft, maxWidth: 132, textAlign: 'center' },
    kicker: { fontFamily: DISPLAY_FONT, fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: t.gold, textAlign: 'center' },
    prompt: { fontFamily: DISPLAY_FONT, fontSize: 22, color: t.ivory, textAlign: 'center', letterSpacing: 0.6 },
    options: { width: '100%', maxWidth: 360, gap: 10 },
    option: {
      borderRadius: 22,
      paddingHorizontal: 16,
      paddingVertical: 12,
    },
    optionText: { fontSize: 14, color: t.ivory },
    footer: { maxWidth: 360, fontSize: 11, color: t.ivoryFaint, textAlign: 'center' },
    finishButton: {
      borderRadius: 999,
      borderWidth: 1,
      borderColor: t.maladyRed,
      backgroundColor: t.glass,
      paddingHorizontal: 20,
      paddingVertical: 10,
    },
    finishButtonText: { fontSize: 13, fontWeight: '800', color: t.maladyText, letterSpacing: 0.3 },
    resultLine: { maxWidth: 360, fontSize: 13, color: t.ivorySoft, textAlign: 'center' },
    doneButton: {
      borderRadius: 999,
      backgroundColor: t.gold,
      boxShadow: `0 6px 18px ${t.goldGlow}`,
      paddingHorizontal: 28,
      paddingVertical: 12,
    },
    doneButtonText: { fontSize: 14, fontWeight: '800', color: t.onGold, letterSpacing: 0.4 },
  }),
);
